// import { setLoading } from "@/redux/slice/loading";
import { errorToast, successToast } from "./toast";

const baseURL = `${process.env.NEXT_PUBLIC_BASE_URL}/api`;

const GENERIC_ERROR_MESSAGE = "An error occurred while making the request.";

// Used for failures we have no server message for: the request never reached a
// response (network down, CORS, abort) or the body could not be read.
const reportFailure = (error) => {
  errorToast(GENERIC_ERROR_MESSAGE);
  return Promise.reject(error);
};

// The single place a response becomes a toast. It is passed to `then` together
// with `reportFailure` as the rejection handler rather than followed by a
// `.catch`, so the rejection raised here can't be reported a second time.
const handleResponse = async (response, { announceSuccess = false } = {}) => {
  if (!response.ok) {
    let error;
    try {
      error = await response.json();
    } catch (parseError) {
      return reportFailure(parseError);
    }

    errorToast(error?.message || GENERIC_ERROR_MESSAGE);
    return Promise.reject(error);
  }

  try {
    const success = await response.json();
    if (announceSuccess) {
      successToast(success.message);
    }
    return success;
  } catch (bodyError) {
    return reportFailure(bodyError);
  }
};

const publicApiCall = {
  async get(url) {
    const requestHeaders = {
      "Content-Type": "application/json",
    };

    const overlay = document.getElementById("overlay");
    if (overlay) overlay.style.display = "block";

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${baseURL}/${url}`, {
        method: "GET",
        headers: new Headers(requestHeaders),
      });
      // Returned, not awaited: a rejection from handleResponse has already been
      // reported there and must not fall into the catch below as a second toast.
      return handleResponse(response);
    } catch (networkError) {
      // Only reached when fetch itself rejects - no response ever arrived.
      return reportFailure(networkError);
    } finally {
      if (overlay) overlay.style.display = "none";
    }
  },

  async post(url, data) {
    const requestHeaders = {
      "Content-Type": "application/json",
    };

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    return fetch(`${baseURL}/${url}`, {
      method: "POST",
      headers: new Headers(requestHeaders),
      body: JSON.stringify(data),
    }).then((response) => handleResponse(response), reportFailure);
  },

  async postPublic(url, data) {
    return fetch(`${baseURL}/${url}`, {
      method: "POST", // You can change the HTTP method as needed (e.g., POST, PUT, DELETE, etc.)
      headers: new Headers({
        "Content-Type": "application/json", // Set Content-Type to JSON
      }),
      body: JSON.stringify(data),
    }).then(
      (response) => handleResponse(response, { announceSuccess: true }),
      reportFailure
    );
  },

  async put(url, data) {
    const requestHeaders = {
      "Content-Type": "application/json",
    };

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    return fetch(`${baseURL}/${url}`, {
      method: "PUT", // You can change the HTTP method as needed (e.g., POST, PUT, DELETE, etc.)
      headers: new Headers(requestHeaders),
      body: JSON.stringify(data),
    }).then((response) => handleResponse(response), reportFailure);
  },

  async patch(url, data) {
    const requestHeaders = {
      "Content-Type": "application/json",
    };

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    return fetch(`${baseURL}/${url}`, {
      method: "PATCH",
      headers: new Headers(requestHeaders),
      body: JSON.stringify(data),
    }).then((response) => handleResponse(response), reportFailure);
  },

  async postWithAuth(url, data) {
    const requestHeaders = {
      "Content-Type": "application/json",
    };

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    return fetch(`${baseURL}/${url}`, {
      method: "POST",
      headers: new Headers(requestHeaders),
      body: JSON.stringify(data),
    }).then(
      (response) => handleResponse(response, { announceSuccess: true }),
      reportFailure
    );
  },

  async postFormData(url, formData) {
    const requestHeaders = {};

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData, let browser set it with boundary

    return fetch(`${baseURL}/${url}`, {
      method: "POST",
      headers: new Headers(requestHeaders),
      body: formData, // Send FormData directly
    }).then(
      (response) => handleResponse(response, { announceSuccess: true }),
      reportFailure
    );
  },

  async putFormData(url, formData) {
    const requestHeaders = {};

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData, let browser set it with boundary

    return fetch(`${baseURL}/${url}`, {
      method: "PUT",
      headers: new Headers(requestHeaders),
      body: formData, // Send FormData directly
    }).then(
      (response) => handleResponse(response, { announceSuccess: true }),
      reportFailure
    );
  },

  async delete(url) {
    const requestHeaders = {
      "Content-Type": "application/json",
    };

    const token = localStorage.getItem("access_token");
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    return fetch(`${baseURL}/${url}`, {
      method: "DELETE",
      headers: new Headers(requestHeaders),
    }).then(
      (response) => handleResponse(response, { announceSuccess: true }),
      reportFailure
    );
  },
};

export default publicApiCall;
